import React, { useState, useCallback, useEffect } from 'react';
import { useAppStore } from '@/store';
import * as api from '@/api/client';
import type { Scores, QaQuestion } from '@/types';
import {
  Upload, FileText, File, AlertTriangle,
  CheckCircle, ArrowRight, Loader2, Zap, Lock, Trash2, Image as ImageIcon,
  ChevronLeft, ChevronRight, RefreshCcw, X,
} from 'lucide-react';
import type { MatterDocument } from '@/api/client';
import TabBar, { type Stage1Tab } from './Stage1/TabBar';
import SummaryTab, { decodeEditedFields } from './Stage1/SummaryTab';
import TechBasicsTab, {
  computeTechBasicsReadTime,
  type TechnicalUnderstanding,
} from './Stage1/TechBasicsTab';
import SmartQATab, { countAnswered } from './Stage1/SmartQATab';
import DrafterNotesTab from './Stage1/DrafterNotesTab';
import FiguresTab from './Stage1/FiguresTab';
import StageAcknowledgment from '@/components/workspace/StageAcknowledgment';
// Shared upload-side components — the editable copies live in
// Stage 0; Stage 1 mounts the readonly UploadedFilesList here.
import UploadedFilesList from '@/components/upload/UploadedFilesList';
import ExtractedFiguresStatus from '@/components/upload/ExtractedFiguresStatus';

function ScoreBar({ label, value }: { label: string; value: number }) {
  const color = value >= 80 ? 'bg-green-500' : value >= 40 ? 'bg-[#002FA7]' : 'bg-amber-500';
  return (
    <div className="flex items-center gap-3 text-sm" data-testid={`score-bar-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <span className="w-32 text-zinc-600 truncate">{label}</span>
      <div className="flex-1 h-2.5 bg-zinc-100 rounded-sm overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${value}%` }} />
      </div>
      <span className="w-10 text-right font-mono text-xs text-zinc-700">{value}%</span>
    </div>
  );
}

function PageIndexRow({
  doc, onRetry, retrying,
}: {
  doc: MatterDocument;
  onRetry: () => void;
  retrying: boolean;
}) {
  const status = doc.status;
  // Human-friendly label + colour per status. "processing" covers legacy
  // rows indexed under the old pipeline where we didn't split the
  // extract/index phases.
  let label = '';
  let sub = '';
  let Icon: React.ComponentType<{ size?: number; className?: string }> = Loader2;
  let iconClass = 'text-zinc-500 animate-spin';
  let chipClass = 'bg-blue-50 border-blue-200 text-[#002FA7]';
  if (status === 'uploaded') {
    label = 'Queued';
    sub = 'Waiting for PageIndex worker';
    Icon = Loader2; iconClass = 'text-zinc-500 animate-spin';
  } else if (status === 'extracting') {
    label = 'Extracting text…';
    Icon = Loader2; iconClass = 'text-zinc-500 animate-spin';
  } else if (status === 'indexing' || status === 'processing') {
    label = 'Indexing with PageIndex…';
    sub = 'Usually 1–3 minutes';
    Icon = Loader2; iconClass = 'text-[#002FA7] animate-spin';
  } else if (status === 'indexed') {
    // Per-format summary: PPTX → "8 slides, 5 images described";
    // PDF → "12 pages, 3 images described"; PNG/JPG → "figure
    // described by AI"; TXT → "indexed". Falls back to the generic
    // label when format metadata is missing (legacy rows).
    const fmt = (doc as any).file_format as string | undefined;
    const slides = (doc as any).slide_count as number | null | undefined;
    const pages = (doc as any).page_count as number | null | undefined;
    const imgs = (doc as any).image_count as number | null | undefined;
    const visioned = (doc as any).vision_processed;
    const parts: string[] = [];
    if (fmt === 'pptx' && typeof slides === 'number' && slides > 0) {
      parts.push(`${slides} slide${slides === 1 ? '' : 's'}`);
    } else if (fmt === 'pdf' && typeof pages === 'number' && pages > 0) {
      parts.push(`${pages} page${pages === 1 ? '' : 's'}`);
    }
    if (visioned && typeof imgs === 'number' && imgs > 0) {
      parts.push(`${imgs} image${imgs === 1 ? '' : 's'} described`);
    } else if (fmt && ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'webp'].includes(fmt)) {
      parts.push('figure described by AI');
    }
    label = parts.length ? `Indexed — ${parts.join(', ')}` : 'Indexed — Ready';
    Icon = CheckCircle; iconClass = 'text-green-600';
    chipClass = 'bg-green-50 border-green-200 text-green-800';
  } else if (status === 'failed') {
    label = 'Indexing failed';
    Icon = AlertTriangle; iconClass = 'text-amber-600';
    chipClass = 'bg-amber-50 border-amber-200 text-amber-800';
  } else {
    label = status;
  }
  return (
    <li
      data-testid={`pageindex-row-${doc.id}`}
      data-status={status}
      className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-sm border ${chipClass}`}
    >
      <Icon size={14} className={`flex-shrink-0 ${iconClass}`} />
      <span className="truncate flex-1 text-zinc-800">{doc.doc_name}</span>
      <span className="text-xs font-mono">
        {label}
        {sub && <span className="ml-2 text-[10px] text-zinc-500">{sub}</span>}
      </span>
      {status === 'failed' && (
        <button
          type="button"
          data-testid={`pageindex-retry-${doc.id}`}
          onClick={onRetry}
          disabled={retrying}
          className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 disabled:opacity-50"
        >
          {retrying ? <Loader2 size={10} className="animate-spin" /> : <RefreshCcw size={10} />}
          Retry
        </button>
      )}
    </li>
  );
}

function PageIndexCompletionToast({
  onDismiss,
}: {
  onDismiss: () => void;
}) {
  return (
    <div
      data-testid="pageindex-completion-toast"
      className="fixed bottom-6 right-6 z-40 bg-white border border-green-200 rounded-sm shadow-md px-4 py-3 flex items-center gap-3 text-sm"
    >
      <CheckCircle size={16} className="text-green-600" />
      <span className="text-zinc-800">
        <span className="font-medium">All documents indexed.</span>{' '}
        PageIndex grounding active.
      </span>
      <button
        type="button"
        onClick={onDismiss}
        className="text-zinc-400 hover:text-zinc-700"
      >
        <X size={14} />
      </button>
    </div>
  );
}



/** Jurisdiction selector. Display-only chip with Lock icon when the
 *  matter's claims are frozen (changing jurisdiction post-freeze
 *  silently invalidates Stage 3's formal-requirements gate); clickable
 *  EP/US/IN choice otherwise. Uses the existing PATCH /api/matter/:id
 *  endpoint which already enforces the same lock server-side. */
function JurisdictionSelector({
  matterId, value, locked, onSaved, onError,
}: {
  matterId: string;
  value: string | undefined;
  locked: boolean;
  onSaved: () => Promise<void> | void;
  onError: (msg: string | null) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const choices: Array<'EP' | 'US' | 'IN'> = ['EP', 'US', 'IN'];

  const save = async (next: 'EP' | 'US' | 'IN') => {
    if (locked || next === value) return;
    setBusy(next);
    onError(null);
    try {
      await api.updateMatter(matterId, { jurisdiction: next });
      await onSaved();
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Failed to update jurisdiction');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="jurisdiction-selector">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Jurisdiction</span>
        {locked ? (
          <span
            data-testid="jurisdiction-locked"
            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-800 border border-zinc-200"
            title="Locked — claims have been frozen"
          >
            {value}
            <Lock size={10} className="text-zinc-400" />
          </span>
        ) : (
          <div className="flex items-center gap-1">
            {choices.map((c) => {
              const active = c === value;
              const loading = busy === c;
              return (
                <button
                  key={c}
                  type="button"
                  data-testid={`jurisdiction-${c}`}
                  onClick={() => save(c)}
                  disabled={!!busy || active}
                  className={
                    'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ' +
                    (active
                      ? 'bg-[#002FA7] text-white border-[#002FA7]'
                      : 'bg-white text-zinc-700 border-zinc-300 hover:border-[#002FA7]')
                    + ' disabled:opacity-50'
                  }
                >
                  {loading ? <Loader2 size={10} className="animate-spin inline" /> : c}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Stage1() {
  const { matterState, setMatterState, resetBanner, setResetBanner, setActiveStage } = useAppStore();
  const matter = matterState?.matter;
  const matterId = matter?.id || '';
  const s1 = matterState?.stages?.stage_1;
  const content = s1?.content || {};
  const isFrozen = s1?.is_frozen;

  // Right-panel tab selection. Defaults to 'summary' so a fresh Stage 1
  // lands on the AI-extracted summary. Subsequent sub-prompts will feed
  // tab content in; today the content area is still empty.
  const [activeTab, setActiveTab] = useState<Stage1Tab>('summary');

  // Figures count for the TabBar badge. Hoisted out of FiguresTab so
  // the badge stays accurate even when the drafter is on a different
  // tab. Loaded lazily once on matter-mount and then kept in sync via
  // FiguresTab's onCountChange callback (after a discard / refresh).
  const [figuresCount, setFiguresCount] = useState(0);

  // ``uploading`` removed — the upload zone now lives in Stage 0
  // (see backend/queue.py spec note). Stage 1 only consumes the
  // resulting docs via the readonly UploadedFilesList below.
  const [scoring, setScoring] = useState(false);
  const [generatingQa, setGeneratingQa] = useState(false);
  const [submittingAnswers, setSubmittingAnswers] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `currentQa` mirrors the single "question set" returned by the backend.
  // The PRD's original 3-round flow was replaced with a single-shot full
  // question list (user request): generate once, answer any subset you like,
  // skip the rest. Regenerate replaces the whole set.
  const [currentQa, setCurrentQa] = useState<{ round_number: number; questions: QaQuestion[]; empty_reason?: string | null } | null>(null);
  const [answers, setAnswers] = useState<Record<number, { selected_option?: string; free_text: string }>>({});

  const scores: Scores = content.scores || {};
  const summary = content.summary || {};
  const overallScore = content.overall_score || 0;
  const qaRounds = matterState?.qa_rounds || [];
  const technicalUnderstanding = (content as any).technical_understanding as TechnicalUnderstanding | undefined;
  const drafterNotes: string = (s1?.content as any)?.drafter_notes || '';
  // Routing telemetry surfaced by score_stage_1 — drives the
  // truncation banner shown when a very large IDF was processed
  // and either the chunker or the fast-path safety cap dropped
  // content. Banner only fires when context_info.truncated is true.
  const contextInfo: {
    char_count?: number;
    est_tokens?: number;
    path?: 'fast' | 'chunked';
    chunks_processed?: number;
    chunks_total?: number;
    truncated?: boolean;
    truncated_chunks?: number;
  } = (content as any).context_info || {};
  const editedFieldCount = decodeEditedFields((s1 as any)?.idf_edited_fields).length;

  // Unanswered-Q&A count for the TabBar badge. Mirrors the same
  // "has the drafter typed anything or picked a chip?" check the
  // submit button uses (see SmartQATab.countAnswered), so the two
  // stay in sync without needing to duplicate the filter here.
  const qaUnansweredCount = currentQa
    ? Math.max(0, currentQa.questions.length - countAnswered(answers))
    : 0;

  // Potential overall-score uplift if the drafter answers the
  // currently-unanswered Q&A questions. Weights match the backend's
  // `_compute_overall_score` (problem/solution 0.30, tech_effect/
  // embodiments 0.20). One question per parameter is counted toward
  // the lift so a parameter with three questions doesn't get
  // 3× weighted credit. Returns null when there's nothing to show.
  const PARAM_WEIGHTS: Record<string, number> = {
    problem: 0.30, solution: 0.30, technical_effect: 0.20, embodiments: 0.20,
  };
  const potentialFromQa = (() => {
    if (!currentQa || qaUnansweredCount === 0) return null;
    const answeredCount = countAnswered(answers);
    const unanswered = currentQa.questions.filter((_, i) => {
      const a = answers[i];
      return !(a && (a.selected_option || (a.free_text || '').trim()));
    });
    const seen = new Set<string>();
    let lift = 0;
    for (const q of unanswered) {
      if (seen.has(q.parameter)) continue;
      seen.add(q.parameter);
      const w = PARAM_WEIGHTS[q.parameter];
      if (!w) continue;
      const cur = (scores as any)[q.parameter] || 0;
      lift += w * (100 - cur);
    }
    if (lift <= 0) return null;
    return {
      potential: Math.min(100, Math.round(overallScore + lift)),
      questionCount: qaUnansweredCount,
      _answered: answeredCount,
    };
  })();

  // On stage entry (or after a matter refresh), rehydrate the Q&A card from
  // the persisted round so the drafter sees their previously-generated
  // Auto-dismiss the post-reset banner after 8 s — long enough to read,
  // short enough not to pile up if the drafter resets repeatedly. The
  // [×] button on the banner provides the same exit immediately. The
  // effect is keyed on the banner identity so it restarts cleanly when
  // a new reset happens before the previous timer fires.
  useEffect(() => {
    if (!resetBanner) return;
    const t = setTimeout(() => setResetBanner(null), 8000);
    return () => clearTimeout(t);
  }, [resetBanner, setResetBanner]);

  // questions and any answers they already picked. Without this, navigating
  // away and back would show an empty Q&A card even though the DB has rows.
  useEffect(() => {
    if (currentQa) return;  // explicit state wins
    const latest = qaRounds[qaRounds.length - 1];
    if (!latest || !Array.isArray(latest.questions) || latest.questions.length === 0) return;
    setCurrentQa({ round_number: latest.round_number, questions: latest.questions });
    const prefill: Record<number, { selected_option?: string; free_text: string }> = {};
    latest.questions.forEach((q: any, i: number) => {
      if (q.selected_answer || q.free_text) {
        prefill[i] = { selected_option: q.selected_answer, free_text: q.free_text || '' };
      }
    });
    setAnswers(prefill);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qaRounds.length]);

  const refreshMatter = useCallback(async () => {
    const data = await api.getMatter(matterId);
    setMatterState(data);
  }, [matterId, setMatterState]);

  // Figures-count fetch so the Figures tab badge reflects reality
  // even before the drafter clicks the tab. Re-runs whenever
  // `figuresReloadToken` bumps (see below) so a second document
  // finishing indexing updates the badge without a manual refresh.
  // After the tab is open, FiguresTab keeps figuresCount fresh via
  // onCountChange (so a discard immediately drops the badge by one).
  // The initial fetch tolerates failure silently — no figures
  // persisted yet is the common case for a brand-new matter and
  // shouldn't surface as an error toast.
  const [figuresReloadToken, setFiguresReloadToken] = useState(0);
  useEffect(() => {
    if (!matterId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.listMatterFigures(matterId);
        if (!cancelled) setFiguresCount(resp.figure_count || 0);
      } catch {
        if (!cancelled) setFiguresCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [matterId, figuresReloadToken]);

  // ─── PageIndex document status polling ──────────────────────────
  // Backend statuses: uploaded | extracting | indexing | processing |
  // indexed | failed | archived. We treat the first four as "in
  // flight" and poll every 5s until everything settles. Non-archived
  // only — archived rows are prior IDFs already superseded.
  // Stage 1 now consumes pageIndexDocs as a derived signal — the
  // shared UploadedFilesList component owns the polling and feeds
  // us each fresh list via its onDocumentsChange callback. We keep
  // the local state because the title-bar PageIndex badge below +
  // the completion-toast logic + the figures-reload bump all read
  // it. Upload-side state (uploading, optimisticUploads) lived
  // here when the dropzone did too; with uploads moved to Stage 0
  // those went with it.
  const [pageIndexDocs, setPageIndexDocs] = useState<MatterDocument[]>([]);
  const [completionToast, setCompletionToast] = useState(false);

  // Track previous indexing-in-flight count so we can show the "all
  // documents indexed" toast the moment the last one flips to
  // 'indexed' — not on first load when everything is already settled.
  const prevInFlightRef = React.useRef<number>(0);
  // Track previous indexed-count so we can detect the specific edge
  // case "a new document just reached indexed status" (could happen
  // while other docs are still processing). When it bumps we signal
  // the figures fetchers to re-run — new figures may have landed
  // with the newly-indexed doc, and without this the Figures tab
  // grid + badge would stay stuck on the pre-upload numbers until
  // the drafter navigates away and back.
  const prevIndexedCountRef = React.useRef<number>(0);

  // ``UploadedFilesList`` (mounted in the left panel below) owns the
  // 5s polling + the listMatterDocuments fetch. We subscribe via its
  // onDocumentsChange callback below so the title-bar PageIndex
  // badge / completion toast / figures-reload token side-effects
  // continue to fire from the same source of truth.
  const handleDocumentsChange = useCallback((docs: MatterDocument[]) => {
    setPageIndexDocs(docs);
    const inFlight = docs.filter(
      (d) => d.status !== 'indexed' && d.status !== 'failed',
    ).length;
    const indexedCount = docs.filter((d) => d.status === 'indexed').length;
    const hasFailures = docs.some((d) => d.status === 'failed');
    if (
      prevInFlightRef.current > 0 &&
      inFlight === 0 &&
      docs.length > 0 &&
      !hasFailures
    ) {
      setCompletionToast(true);
      setTimeout(() => setCompletionToast(false), 5000);
    }
    // New document transitioned to 'indexed' → bump the figures
    // reload token so the badge + grid refetch. Strictly ">" so a
    // restart of the poller where prev was 0 and we now see N
    // legacy indexed docs doesn't trigger a spurious refetch on
    // matter mount.
    if (indexedCount > prevIndexedCountRef.current) {
      setFiguresReloadToken((t) => t + 1);
    }
    prevInFlightRef.current = inFlight;
    prevIndexedCountRef.current = indexedCount;
  }, []);

  // ``uploadProgress`` removed alongside the upload zone — the
  // shared UploadZone (mounted in Stage 0) owns its own progress
  // banner. Stage 1 doesn't see uploads any more.

  // ── Left panel collapse ──────────────────────────────────────────
  // Border-chevron toggle slides the left rail to width 0 so the
  // drafter gets the full horizontal space for the tab content. The
  // whole animation lives in CSS (see .stage1-left-panel/.collapsed);
  // this state just flips the class and is persisted per-browser so
  // the preference sticks across refreshes.
  const [leftCollapsed, setLeftCollapsed] = useState<boolean>(() => {
    try {
      // Prefer the new key; migrate the old one if a drafter had it set
      // before the collapse pattern was rewired to the border toggle.
      const next = localStorage.getItem('stage1_left_panel_collapsed');
      if (next !== null) return next === 'true';
      const legacy = localStorage.getItem('stage1_upload_panel_collapsed');
      return legacy === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('stage1_left_panel_collapsed', leftCollapsed ? 'true' : 'false');
      // Keep the legacy key in sync so a rollback wouldn't lose the
      // preference; cheap, one write on every toggle.
      localStorage.setItem('stage1_upload_panel_collapsed', leftCollapsed ? 'true' : 'false');
    } catch {}
  }, [leftCollapsed]);
  // A fresh matter (no uploads yet) needs the upload dropzone visible
  // — otherwise the drafter lands on a blank right panel and can't
  // tell how to start. Force the rail open on mount whenever the
  // matter has no IDF documents, regardless of the persisted
  // preference. Subsequent collapses are honoured.
  const hasDocumentsOnMount = (matterState?.idf_documents || []).length > 0;
  useEffect(() => {
    if (!hasDocumentsOnMount) setLeftCollapsed(false);
    // Run once on mount only — the persisted preference takes over
    // from here, so later uploads/deletes don't override the drafter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ``visionMode``, ``handleUpload``, ``stringifyUploadError``,
  // ``inFlightRef``, ``optimisticUploads``, the auto-expand-on-upload
  // effect — all moved out alongside the upload zone. The shared
  // ``UploadZone`` component (rendered in Stage 0) owns those now.
  // Stage 1 only consumes the resulting docs through the readonly
  // ``UploadedFilesList`` mounted in the left panel below.

  // Delete + retry handlers moved into UploadedFilesList alongside
  // the dropzone. The readonly Stage 1 mount of UploadedFilesList
  // doesn't render those affordances, so Stage 1 doesn't need the
  // handlers at all any more.

  const handleScore = async () => {
    setScoring(true);
    setError(null);
    try {
      await api.scoreStage1(matterId);
      await refreshMatter();
    } catch (e: any) {
      // Prefer the server's own detail when present — the backend
      // now returns actionable messages for LLM-level failures. Fall
      // back to a drafter-friendly retry prompt when the server
      // returned an opaque 500. "Check LLM settings" used to bury
      // the drafter in a dead end.
      setError(
        e.response?.data?.detail
        || 'Scoring incomplete — click Analyze IDF to retry.',
      );
    }
    setScoring(false);
  };

  const handleGenerateQa = async () => {
    setGeneratingQa(true);
    setError(null);
    try {
      const result = await api.generateQa(matterId);
      setCurrentQa(result);
      setAnswers({});
      await refreshMatter();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Q&A generation failed');
      // Re-throw so SmartQATab's wrappedGenerate can flip its local
      // qaFailed flag and switch the button into Retry mode. Without
      // this, a caught-and-swallowed error left the tab showing the
      // pristine "not yet generated" state with a Generate button —
      // obscuring that the user already tried and the model failed.
      throw e;
    } finally {
      setGeneratingQa(false);
    }
  };

  const handleSubmitAnswers = async () => {
    if (!currentQa) return;
    setSubmittingAnswers(true);
    setError(null);
    try {
      const answerList = currentQa.questions.map((_, i) => ({
        question_index: i,
        selected_option: answers[i]?.selected_option || null,
        free_text: answers[i]?.free_text || '',
      }));
      await api.submitAnswers(matterId, answerList);
      // DON'T clear currentQa — the answers endpoint updates the
      // existing qa_round (same round_number, same length) rather
      // than appending a new one, so the rehydration useEffect that
      // keys on ``qaRounds.length`` never refires. Setting
      // currentQa=null would drop the drafter back to the
      // "Questions not yet generated" empty state even though the
      // backend has their answers persisted. Instead, re-fetch the
      // matter state fresh and mirror the updated round + answers
      // back into local state so the panel shows the submitted
      // values alongside the new scores.
      const fresh = await api.getMatter(matterId);
      setMatterState(fresh);
      const rounds = (fresh as any)?.qa_rounds || [];
      const latest = rounds[rounds.length - 1];
      if (latest && Array.isArray(latest.questions) && latest.questions.length > 0) {
        setCurrentQa({
          round_number: latest.round_number,
          questions: latest.questions,
        });
        // Mirror any server-persisted answers back into the local
        // answers map so each <textarea> shows what the drafter
        // submitted (component reads from `answers[i].free_text`,
        // not from the question's own server fields).
        const prefill: Record<number, { selected_option?: string; free_text: string }> = {};
        latest.questions.forEach((q: any, i: number) => {
          if (q?.selected_answer || q?.free_text) {
            prefill[i] = {
              selected_option: q.selected_answer || undefined,
              free_text: q.free_text || '',
            };
          }
        });
        setAnswers(prefill);
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to submit answers');
    }
    setSubmittingAnswers(false);
  };

  const handleFreeze = async () => {
    setFreezing(true);
    setError(null);
    try {
      await api.freezeStage1(matterId);
      await refreshMatter();
      // Auto-advance to Stage 2 so the drafter doesn't have to click
      // the stage chip in the top bar themselves. Mirrors the
      // single-click behaviour of Stage 2's Freeze Both & Continue.
      setActiveStage('stage_2');
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Cannot freeze Stage 1');
    }
    setFreezing(false);
  };

  const hasDocuments = (matterState?.idf_documents || []).length > 0;
  const hasScores = !!content.scores;
  // Soft gate: any scoring attempt unlocks the freeze button. A
  // score of 0 on every field (transient LLM parse failure) triggers
  // a yellow "proceed anyway" warning banner rather than a hard
  // block — the backend freeze endpoint mirrors this (only rejects
  // when the scores dict is missing entirely).
  const canFreeze = hasScores;
  // epi compliance Item 2 — review-acknowledged state. AND-ed with
  // canFreeze in the button disabled logic below; the ack alone
  // never unlocks the gate, the gate alone never advances.
  const persistedAckAt: string | null = (content as any)?.acknowledged_at || null;
  const [stageAckAt, setStageAckAt] = useState<string | null>(persistedAckAt);
  useEffect(() => {
    // Re-sync when the matter blob refreshes (handleScore /
    // refreshMatter paths). Doing it in an effect avoids fighting
    // the user's click while the POST is in flight — optimistic
    // state lives inside StageAcknowledgment itself.
    setStageAckAt(persistedAckAt);
  }, [persistedAckAt]);
  const allZeroScores = hasScores && (
    (scores.problem || 0) === 0 &&
    (scores.solution || 0) === 0 &&
    (scores.technical_effect || 0) === 0 &&
    (scores.embodiments || 0) === 0 &&
    ((scores as any).advantages || 0) === 0
  );

  return (
    <>
    <div className="stage1-layout">
      {/* Border chevron toggle — sits on the seam between the left
          and right panels so the drafter always has one obvious
          affordance to collapse or expand the rail. Styled and
          positioned entirely in CSS (.stage1-panel-toggle); this
          component just flips the state. */}
      <button
        type="button"
        data-testid="stage-1-panel-toggle"
        onClick={() => setLeftCollapsed(v => !v)}
        title={leftCollapsed ? 'Expand panel' : 'Collapse panel'}
        aria-label={leftCollapsed ? 'Expand panel' : 'Collapse panel'}
        aria-expanded={!leftCollapsed}
        className={`stage1-panel-toggle${leftCollapsed ? ' collapsed' : ''}`}
      >
        {leftCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
      <div
        data-testid="stage-1-left-panel"
        className={`stage1-left-panel${leftCollapsed ? ' collapsed' : ''}`}
      >
        {/* Stage header — title + a row of compact status badges. The
            PageIndex badge (✓ green / ⏳ amber / ✗ red) summarises the
            state of all per-document indexing rows in one glance so
            drafters notice failures even when the Files panel is
            collapsed. Mirrors the per-row PageIndex card below; never
            hides any failure information. */}
        <div className="flex items-start justify-between gap-3 mb-1">
          <h1
            data-testid="stage-1-title"
            className="text-lg font-semibold tracking-tight text-zinc-900"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Stage 1 — IDF Understanding
          </h1>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Collapse toggle now lives on the border between the
                panels (see .stage1-panel-toggle); no in-panel copy
                here any more. */}
            {(() => {
              if (pageIndexDocs.length === 0) return null;
              const indexed = pageIndexDocs.filter(d => d.status === 'indexed').length;
              const failed = pageIndexDocs.filter(d => d.status === 'failed').length;
              const inflight = pageIndexDocs.length - indexed - failed;
              let label = 'PageIndex ✓';
              let cls = 'bg-green-50 text-green-800 border-green-200';
              if (failed > 0) {
                label = 'PageIndex ✗';
                cls = 'bg-red-50 text-red-800 border-red-200';
              } else if (inflight > 0) {
                label = 'PageIndex ⏳';
                cls = 'bg-amber-50 text-amber-800 border-amber-200';
              }
              return (
                <span
                  data-testid="stage-1-pageindex-badge"
                  data-status={failed > 0 ? 'failed' : (inflight > 0 ? 'inflight' : 'ok')}
                  className={`inline-flex items-center text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${cls}`}
                  title={`${indexed} indexed · ${inflight} in flight · ${failed} failed`}
                >
                  {label}
                </span>
              );
            })()}
            {hasScores && (
              <span className="inline-flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full border bg-green-50 text-green-800 border-green-200">
                Scored · {Math.round((content.overall_score || 0))}%
              </span>
            )}
          </div>
        </div>
        <p
          className="text-xs text-zinc-500 mb-4"
          title="Upload your Invention Disclosure Form and assess its completeness"
        >
          Upload your Invention Disclosure Form and assess its completeness
        </p>

        {/* Post-reset success banner. The banner state lives in the
            global store (set by Stage 3's reset handler immediately
            before flipping activeStage to stage_1) and is read here
            because Stage 3 unmounts on the navigation. Auto-dismisses
            after 8 s; the [×] button is also available. */}
        {resetBanner && (
          <div
            data-testid="reset-banner"
            className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm text-sm text-blue-900 flex items-start gap-2"
          >
            <CheckCircle size={16} className="mt-0.5 flex-shrink-0 text-blue-700" />
            <span className="flex-1">
              Matter reset. {resetBanner.count > 0 && `${resetBanner.count} claim${resetBanner.count === 1 ? '' : 's'} deleted. `}
              Your documents are still indexed — you can start drafting immediately.
            </span>
            <button
              type="button"
              data-testid="reset-banner-dismiss"
              onClick={() => setResetBanner(null)}
              className="text-blue-700 hover:text-blue-900 flex-shrink-0"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {error && (
          <div
            data-testid="stage-1-error"
            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2"
          >
            <AlertTriangle size={16} />
            {error}
          </div>
        )}

        {/* Left-panel section stack — Upload-link, Analyze-IDF
            (pre-score only) / Completeness (post-score), and the
            Files / Figures / Jurisdiction trio. Upload zone moved to
            Stage 0; this stage's left rail is read-only. Panel-level
            collapse lives on the border toggle (.stage1-panel-toggle). */}
        <div className="space-y-6 mb-6">
          {/* Upload-more link — sits where the dropzone used to.
              Routes back to the Stage 0 tab so drafters can add
              supplementary docs without scrolling away from Stage 1. */}
          <button
            type="button"
            data-testid="stage-1-upload-more-link"
            onClick={() => setActiveStage('stage_0')}
            className="w-full text-left text-xs text-[#002FA7] hover:text-[#002277] hover:underline inline-flex items-center gap-1.5 px-1 py-1.5"
            title="Open the Stage 0 upload screen"
          >
            <Upload size={12} className="flex-shrink-0" />
            ← Upload more documents
          </button>


          {/* Upload-gated hints — show exactly one guide line under the
              upload zone so the drafter always knows the next action:
              upload first, then analyze. Silent once scoring has run. */}
          {!hasDocuments && !isFrozen && (
            <button
              type="button"
              data-testid="stage-1-empty-hint"
              onClick={() => setActiveStage('stage_0')}
              className="text-xs text-zinc-500 hover:text-[#002FA7] hover:underline text-center w-full"
              title="Open the Stage 0 upload screen"
            >
              No documents yet — upload from Stage 0 to begin
            </button>
          )}
          {hasDocuments && !hasScores && !isFrozen && (
            <div
              data-testid="stage-1-needs-score-hint"
              className="text-xs text-zinc-500 text-center"
            >
              Click Analyze IDF to score the document before continuing
            </div>
          )}

          {/* Analyze-IDF — shown pre-scoring once documents exist.
              After the first scoring this button hides and the
              Completeness card below takes its place; the drafter
              re-runs extraction from the Summary tab's Re-extract
              button instead. */}
          {hasDocuments && !hasScores && !isFrozen && (
            <button
              data-testid="analyze-idf-button"
              onClick={handleScore}
              disabled={scoring}
              className="w-full bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-4 py-2.5 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {scoring ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
              {scoring ? 'Analyzing IDF...' : 'Analyze IDF'}
            </button>
          )}

          {/* 3. Completeness score card — always visible when scores exist */}
          {hasScores && (
            <div className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="completeness-scores">
              <div className="flex items-center justify-between mb-4 gap-3">
                <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500">Completeness</h3>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    {Math.round(overallScore)}/100
                  </span>
                  {/* Rescore — re-runs the full IDF analysis (same
                      handler the pre-score Analyze IDF button uses).
                      Mirror of the Re-extract button on the Summary
                      tab; keeping it here means the drafter can
                      re-score from the left rail without scrolling
                      back to the summary. */}
                  {!isFrozen && (
                    <button
                      type="button"
                      data-testid="rescore-button"
                      onClick={handleScore}
                      disabled={scoring}
                      className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1 disabled:opacity-50"
                      title="Re-run completeness analysis on the current IDF"
                    >
                      {scoring ? <Loader2 size={10} className="animate-spin" /> : <RefreshCcw size={10} />}
                      Rescore
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                <ScoreBar label="Problem" value={scores.problem || 0} />
                <ScoreBar label="Solution" value={scores.solution || 0} />
                <ScoreBar label="Tech Effect" value={scores.technical_effect || 0} />
                <ScoreBar label="Embodiments" value={scores.embodiments || 0} />
                {/* Advantages — always visible for visual parity with
                    the rubric. Reads the value from scores.advantages
                    when the LLM emits it; falls back to 0 (empty bar)
                    when it doesn't, which is still informative
                    ("drafter should add advantages detail") rather
                    than hiding the category entirely. */}
                <ScoreBar label="Advantages" value={(scores as any).advantages || 0} />
                {/* Prior art is informational in v1 — shown separately under
                    the summary (when mentioned) rather than scored here. */}
              </div>
            </div>
          )}

          {/* 4 + 5 + 6. Uploaded files (readonly), Figures status,
              Jurisdiction. The editable Uploaded Files block (with
              delete + retry buttons + the in-flight optimistic rows)
              moved to Stage 0 alongside the upload zone. Stage 1's
              left rail shows a readonly mirror so the drafter can
              still see what's been indexed without leaving the
              analysis screen. */}
          <div className="space-y-6">
            {/* 4. Uploaded files — readonly mirror of the editable
                list on Stage 0. UploadedFilesList polls
                listMatterDocuments internally; we subscribe via
                onDocumentsChange so the title-bar PageIndex badge,
                the completion toast, and the figures-reload token
                all keep working from the same source of truth. */}
            <UploadedFilesList
              matterId={matterId}
              mode="readonly"
              isFrozen={!!isFrozen}
              legacyDocsByName={
                new Map(
                  ((matterState?.idf_documents || []) as any[])
                    .map((d) => [d.filename, {
                      extraction_warnings: d.extraction_warnings,
                      extracted_text: d.extracted_text,
                    }]),
                )
              }
              onDocumentsChange={handleDocumentsChange}
            />

            {/* 4b. Extracted figures status — compact one-line summary
                of matter_figures rows. Lives here (between Uploaded
                Files and PageIndex) because it belongs to the
                extraction pipeline that produced the uploaded docs,
                and refreshes whenever the idf_documents list updates
                so the count tracks multi-upload state. */}
            <ExtractedFiguresStatus
              matterId={matterId}
              docCount={(matterState?.idf_documents || []).length}
              refreshKey={figuresReloadToken}
            />

            {/* 5. PageIndex status — REMOVED. Its data is now folded
                into the Uploaded Files block above (status chip +
                inline Retry on each row), so a separate diagnostic
                panel was just visual duplication that hid
                supplementary uploads behind the legacy
                idf_documents-only Uploaded Files view. */}

            {/* 6. Jurisdiction — selectable until claims are frozen */}
            <JurisdictionSelector
              matterId={matterId}
              value={matter?.jurisdiction}
              locked={!!matter?.claims_frozen_at}
              onSaved={refreshMatter}
              onError={setError}
            />
          </div>
        </div>

        {/* Legacy max-w-6xl wrapper + grid removed in the cleanup
            pass; its content was already relocated above into
            .stage1-left-panel and the four right-panel tabs. The
            PageIndex completion toast (fixed-positioned) is now a
            sibling of .stage1-layout so it isn't clipped by the
            fixed-height two-panel shell. */}
      </div>

      <div className="stage1-right-panel">
        {/* Four-tab navigation. Counts are wired to live state above.
            Completeness score lives inline inside the Summary tab's
            own header row — it used to be an absolutely-positioned
            overlay here but kept colliding with tab-local action
            buttons. */}
        <TabBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          summaryEditedCount={0}
          techBasicsReadTime={computeTechBasicsReadTime(technicalUnderstanding)}
          figuresCount={figuresCount}
          qaUnansweredCount={qaUnansweredCount}
          notesCharCount={drafterNotes.length}
          autoSavedAt={null}
        />
        <div className="stage1-tab-content">
          {activeTab === 'summary' && (
            <SummaryTab
              matterId={matterId}
              summary={summary}
              editedFieldsRaw={(s1 as any)?.idf_edited_fields}
              isFrozen={!!isFrozen}
              onRefresh={refreshMatter}
              onError={setError}
              overallScore={hasScores ? overallScore : null}
              potentialFromQa={potentialFromQa}
              onJumpToQa={() => setActiveTab('qa')}
            />
          )}
          {activeTab === 'tech-basics' && (
            <TechBasicsTab
              matterId={matterId}
              isFrozen={!!isFrozen}
              tu={technicalUnderstanding}
              onRefresh={refreshMatter}
              onError={setError}
            />
          )}
          {activeTab === 'figures' && (
            <FiguresTab
              matterId={matterId}
              isFrozen={!!isFrozen}
              onCountChange={setFiguresCount}
              onError={setError}
              reloadToken={figuresReloadToken}
            />
          )}
          {activeTab === 'qa' && (
            <SmartQATab
              matterId={matterId}
              matterTitle={matter?.title || ''}
              scores={scores}
              hasScores={hasScores}
              isFrozen={!!isFrozen}
              generatingQa={generatingQa}
              submittingAnswers={submittingAnswers}
              currentQa={currentQa}
              answers={answers}
              setAnswers={setAnswers}
              handleGenerateQa={handleGenerateQa}
              handleSubmitAnswers={handleSubmitAnswers}
              setError={setError}
            />
          )}
          {activeTab === 'notes' && (
            <DrafterNotesTab
              matterId={matterId}
              initialValue={drafterNotes}
              isFrozen={!!isFrozen}
              onSaved={refreshMatter}
              onError={setError}
            />
          )}
        </div>

        {/* Truncation banner — only appears when score_stage_1 either
            chunked-and-truncated past _MAX_CHUNKS_PER_RUN or hit the
            fast-path safety cap. Sits ABOVE the freeze bar so the
            drafter sees it before locking Stage 1 on a partially-
            analysed IDF. */}
        {contextInfo.truncated && (
          <div
            data-testid="stage-1-context-truncated"
            className="mb-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 leading-snug"
          >
            <div className="font-medium">
              ⚠ Large document — last portion not analysed
            </div>
            <div className="mt-0.5 text-amber-800">
              {contextInfo.path === 'chunked' && (contextInfo.truncated_chunks || 0) > 0
                ? `This IDF has ${contextInfo.chunks_total} chunks but the analyser processed only the first ${contextInfo.chunks_processed}. Last ${contextInfo.truncated_chunks} chunk(s) were not summarised.`
                : `This IDF is ~${(contextInfo.char_count || 0).toLocaleString()} characters; the analysis covers the safe-prompt window only.`}
              {' '}
              Upload a condensed version, split the document, or switch
              to a model with higher token limits for full coverage.
            </div>
          </div>
        )}

        {/* Freeze bar — sticky to the bottom of the right panel so it's
            always visible regardless of which tab is active or how far
            the drafter has scrolled. Same handler, same disabled state
            as the pre-move button; the frozen banner takes its place
            after freeze. When scoring came back all-zero (transient
            LLM parse failure) a yellow "proceed anyway" banner sits
            above the button — the freeze itself is still allowed, so
            the drafter isn't locked in by a one-off pipeline hiccup. */}
        {(hasScores || isFrozen) && (
          <div data-testid="stage-1-freeze-bar" className="stage1-freeze-bar">
            <div className="flex items-center gap-3 text-[11px] text-zinc-500">
              {!isFrozen && hasScores && (
                <span
                  data-testid="stage-1-freeze-score-pill"
                  className={
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono border '
                    + (allZeroScores
                      ? 'bg-amber-50 text-amber-800 border-amber-200'
                      : (overallScore < 40
                        ? 'bg-zinc-100 text-zinc-700 border-zinc-200'
                        : 'bg-green-50 text-green-800 border-green-200'))
                  }
                  title="Overall completeness score across the five rubric fields"
                >
                  Completeness: {Math.round(overallScore)}%
                  {allZeroScores ? ' ⚠' : ''}
                </span>
              )}
              {editedFieldCount > 0 && !isFrozen && (
                <span data-testid="stage-1-freeze-edited-count">
                  {editedFieldCount} of 5 fields edited from AI defaults
                </span>
              )}
              {allZeroScores && !isFrozen && (
                <span
                  data-testid="stage-1-freeze-zero-warning"
                  className="text-amber-800"
                >
                  Scoring returned 0 on every field — may be a transient model issue.
                  You can proceed and add context via Drafter Notes, or click Rescore to retry.
                </span>
              )}
            </div>
            {isFrozen ? (
              <div
                data-testid="stage-1-frozen"
                className="flex items-center gap-3"
              >
                <span className="flex items-center gap-2 text-sm text-green-800">
                  <CheckCircle size={16} />
                  Stage 1 frozen
                </span>
                <button
                  type="button"
                  data-testid="stage-1-continue-to-stage-2"
                  onClick={() => setActiveStage('stage_2')}
                  className="rounded-sm px-3 py-1.5 text-xs font-medium bg-[#002FA7] text-white hover:bg-[#002277] inline-flex items-center gap-1.5"
                >
                  Continue to Stage 2 <ArrowRight size={12} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {allZeroScores && (
                  <button
                    type="button"
                    data-testid="stage-1-freeze-rescore-button"
                    onClick={handleScore}
                    disabled={scoring || freezing}
                    title="Re-run completeness analysis on the current IDF"
                    className="rounded-sm px-3 py-2 text-sm font-medium border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {scoring ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
                    Rescore
                  </button>
                )}
              {/* epi compliance Item 2 — review acknowledgment
                  above the Freeze button. The button's disabled
                  state below ANDs this with canFreeze so the ack
                  alone can't unlock the gate and canFreeze alone
                  can't advance the stage. */}
              <StageAcknowledgment
                matterId={matterId}
                stage="stage_1"
                initialAcknowledgedAt={stageAckAt}
                onChange={setStageAckAt}
                disabled={!!isFrozen}
                onError={setError}
              />
              <button
                data-testid="freeze-stage-1-button"
                onClick={handleFreeze}
                disabled={!canFreeze || !stageAckAt || freezing}
                title={
                  !hasScores
                    ? 'Run Analyze IDF before freezing'
                    : !stageAckAt
                      ? 'Check the review-acknowledged box to continue'
                      : allZeroScores
                        ? 'Scoring returned zero on every field. Proceed anyway — you can rescore later.'
                        : undefined
                }
                className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  canFreeze && stageAckAt
                    ? 'bg-[#002FA7] text-white hover:bg-[#002277]'
                    : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                }`}
              >
                {freezing ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                Freeze Stage 1 &amp; Continue
              </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    {completionToast && (
      <PageIndexCompletionToast onDismiss={() => setCompletionToast(false)} />
    )}
    </>
  );
}
